import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { completePlayerProfile, registerPlayerCompletionHandler } from "src/api/player-requirements";
import { useAuthContext } from "src/auth/hooks";
import { useTranslate } from "src/locales";
import { paths } from "src/routes/paths";
import { PlayerDataProblem, PlayerFieldKey } from "src/types/player-requirements";

const labels: Record<PlayerFieldKey, string> = {
  first_name: "first_name", last_name: "last_name", email: "email", phone: "phone_number", date_of_birth: "birth_date",
};

export default function PlayerDataRequirementsDialog() {
  const { t } = useTranslate();
  const { user, refreshSession } = useAuthContext();
  const [problem, setProblem] = useState<PlayerDataProblem>();
  const [values, setValues] = useState<Partial<Record<PlayerFieldKey, string>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const resolve = useRef<((accepted: boolean) => void) | undefined>();

  const finish = useCallback((accepted: boolean) => {
    resolve.current?.(accepted);
    resolve.current = undefined;
    setProblem(undefined);
  }, []);

  useEffect(() => {
    setProblem(undefined);
    const unregister = registerPlayerCompletionHandler((next) => new Promise<boolean>((done) => {
      resolve.current?.(false);
      resolve.current = done;
      setValues({});
      setError("");
      setProblem(next);
    }));
    return () => { unregister(); resolve.current?.(false); };
  }, [user?.id]);

  const own = problem?.subjects.find((subject) => subject.subject === "self");
  const fields = Array.from(new Set([...(own?.missing_fields ?? []), ...(own?.invalid_fields ?? [])]));
  const otherSubjects = problem?.subjects.filter((subject) => subject.subject !== "self") ?? [];
  const accountEmailRequired = fields.includes("email");

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await completePlayerProfile(values);
      await refreshSession();
      finish(true);
    } catch {
      setError(t("player_data_invalid"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!problem} onClose={() => { if (!busy) finish(false); }} fullWidth maxWidth="sm">
      <DialogTitle>{t("player_required_information")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>{t("player_required_information_description", { provider: problem?.provider_name })}</Typography>
          {otherSubjects.length > 0 && <Alert severity="warning">
            {t("player_data_other_participants")}
            {otherSubjects.map((subject) => <Typography key={subject.subject} variant="body2">
              {subject.subject}: {[...subject.missing_fields, ...subject.invalid_fields].map((key) => t(labels[key])).join(", ")}
            </Typography>)}
          </Alert>}
          {accountEmailRequired && <Alert severity="warning">{t("player_data_account_email")}</Alert>}
          {fields.filter((key) => key !== "email").map((key) => <TextField
            key={key} required fullWidth label={t(labels[key])}
            type={key === "date_of_birth" ? "date" : key === "phone" ? "tel" : "text"}
            InputLabelProps={key === "date_of_birth" ? { shrink: true } : undefined}
            value={values[key] ?? ""}
            onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))}
            inputProps={key === "date_of_birth" ? { max: new Date().toLocaleDateString("en-CA") } : undefined}
          />)}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={() => finish(false)}>{t("cancel")}</Button>
        {accountEmailRequired && <Button href={paths.player.profiledata} onClick={() => finish(false)}>{t("profile_data")}</Button>}
        {fields.length > 0 && !accountEmailRequired && <LoadingButton
          variant="contained" loading={busy}
          disabled={otherSubjects.length > 0 || fields.some((key) => !values[key]?.trim())}
          onClick={save}>{t("player_data_save_continue")}</LoadingButton>}
      </DialogActions>
    </Dialog>
  );
}
